import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, first, map, Observable, switchMap } from 'rxjs';
import { isSameDay } from 'date-fns';
import { ENV_CONFIG, EnvConfig, PLATFORM_TOKEN, PlatformProvider } from '@core';
import { UpdateService } from '@update/infra';
import { SzConfirmDialogService } from '@ui/modal';
import { getNewVersionNative } from './utils';

const LAST_SHOWN_KEY: string = 'splitzero.update.softNudge.lastShownAt';

const I18N_KEYS: Readonly<Record<keyof SoftUpdateStrings, string>> = {
  dialogHeader: 'update.soft.dialogHeader',
  dialogMessage: 'update.soft.dialogMessage',
  downloadCta: 'update.soft.downloadCta',
  laterCta: 'update.soft.laterCta'
};

interface SoftUpdateStrings {
  readonly dialogHeader: string;
  readonly dialogMessage: string;
  readonly downloadCta: string;
  readonly laterCta: string;
}

@Injectable({ providedIn: 'root' })
export class SoftUpdateNudgeService {
  private readonly updateService: UpdateService = inject(UpdateService);
  private readonly confirmDialog: SzConfirmDialogService = inject(SzConfirmDialogService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly platform: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly env: EnvConfig = inject(ENV_CONFIG);

  maybeShow(): void {
    if (!this.platform.isNativeRun) return;
    if (this.wasShownToday()) return;

    this.updateService
      .hasOptionalUpdate()
      .pipe(first())
      .subscribe((available: boolean): void => {
        if (!available) return;
        this.markShown();
        this.show();
      });
  }

  private show(): void {
    this.updateService
      .newestVersion()
      .pipe(
        first(),
        switchMap((version: string) => this.loadStrings(version)),
        switchMap((s: SoftUpdateStrings) =>
          this.confirmDialog.confirm$({
            header: s.dialogHeader,
            message: s.dialogMessage,
            okLabel: s.downloadCta,
            cancelLabel: s.laterCta
          })
        ),
        filter((confirmed: boolean): boolean => confirmed)
      )
      .subscribe((): void => getNewVersionNative(this.env));
  }

  private loadStrings(version: string): Observable<SoftUpdateStrings> {
    const keys: ReadonlyArray<string> = Object.values(I18N_KEYS);
    return this.translate.get(keys as string[], { version }).pipe(
      map(
        (dict: Record<string, string>): SoftUpdateStrings => ({
          dialogHeader: dict[I18N_KEYS.dialogHeader],
          dialogMessage: dict[I18N_KEYS.dialogMessage],
          downloadCta: dict[I18N_KEYS.downloadCta],
          laterCta: dict[I18N_KEYS.laterCta]
        })
      )
    );
  }

  private wasShownToday(): boolean {
    const raw: string | null = localStorage.getItem(LAST_SHOWN_KEY);
    if (!raw) return false;
    const timestamp: number = Number(raw);
    if (!Number.isFinite(timestamp)) return false;
    return isSameDay(new Date(timestamp), new Date());
  }

  private markShown(): void {
    localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
  }
}
